/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useState, useMemo, useContext } from 'react';

import type { ReactNode } from 'react';

type LoadingStates = Record<string, boolean>;
type SetLoading = (id: string, value: boolean) => void;
type RemoveState = (id: string) => void;

type LoadingContextShape = {
    states: LoadingStates;
    loading: boolean;
    setLoading: SetLoading;
    removeState: RemoveState;
};

const initialState: LoadingContextShape = {
    states: {},
    loading: false,
    setLoading: () => {},
    removeState: () => {},
};

export const LoadingContext = createContext(initialState);

export const useLoadingContext = () => useContext(LoadingContext);

export function LoadingProvider({ children }: { children: ReactNode }) {
    const [states, setStates] = useState<LoadingStates>({});
    const loading = useMemo(() => {
        return Object.values(states).some(Boolean);
    }, [states]);

    // ? the id is to be assigned to a certain loading state for
    // ? a component's lifecycle
    const setLoading: SetLoading = (id, value) => {
        setStates((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    // ? to be called before a component unmounts
    const removeState: RemoveState = (id) => {
        setStates((prev) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [id]: _, ...rest } = prev;
            return rest;
        });
    };

    return (
        <LoadingContext.Provider value={{ states, loading, setLoading, removeState }}>
            {children}
        </LoadingContext.Provider>
    );
}
